/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  Fragment,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  Collapse,
  message,
  Tag as TagAntd,
  Popover,
  Tooltip,
  Checkbox,
} from 'antd';
import { CollapseProps } from 'antd/lib/collapse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import ResizeObserver from 'resize-observer-polyfill';
import { debounce } from 'lodash';

// Contexts
import AppContext from '~src/contexts/AppContext';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import LargeFilesContext from '~src/contexts/LargeFilesContext';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';

// Hooks
import { useStyles, ThemedStyleFn } from '~src/hooks/useStyles';
import { useProjectId } from '~src/hooks/useProjectId';
import { useIsPath } from '~src/hooks/useIsPath';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useProject } from '~src/hooks/api-hooks/useProject';
import { useUser } from '~src/hooks/api-hooks/useUser';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';

// Utils
import { formatBytes } from '~src/utils/formatBytes';
import { formatDate } from '~src/utils/formatDate';
import { getPath } from '~src/utils/getFilePath';
import { appLog } from '~src/utils/log';
import { downloadFileLastVersion, downloadFolder } from '~src/utils/download';
import { getTagsForFile } from '~src/utils/tags';

// Types
import { DbFile, DbFolder, DbModuleFile } from '~src/types/db';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import { ClassicRegistration } from '~src/services/api/modules';
import { FileTableProps } from '../FileTables/FileTable';

// Components
import IconButton from '~components/_common/IconButton/IconButton';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';

// Store & Config
import { DownloadingState, useStore } from '~src/store';
import { fileIcons } from '~src/config/fileTypeIcons';
import doReturnTheme from '../../../styles/themeEngine';

// Assets
import ModuleTenderHammerNoColor from '~src/assets/nav-icons/folder-tender-hammer-no-color.svg';

const { Panel } = Collapse;

interface PropertiesPanelProps {
  selectedItem: any;
  /** Collapse state change callback */
  onCollapse?: CollapseProps['onChange'];
  tenderPropertiesTable?: any;
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedItem,
  onCollapse,
  tenderPropertiesTable,
}) => {
  // All hooks must be called unconditionally at the top
  const intl = useIntl();
  const { currentTheme } = useThemeSwitcher();
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { setLargeFilesContext } = useContext(LargeFilesContext);
  const { setFileToView } = useFileViewerContext();
  const { appContextState } = useContext(AppContext);
  const { changeDownloadingActive, changeDownloadingState } = useStore();
  const style = useStyles(styleFn);
  const projectQuery = useProject();
  const archiveSettings = useArchiveSettings();
  const projectId = useProjectId();
  const { currentFolderId, rootFolderId } = useTenderParamsFromUrl();
  const isOnTenders = useIsPath('/tenders');
  const isOnAnyTender = useIsPath('/tenders');
  const isOnMyTenders = useIsPath('/my-tenders');
  const isOnTenderRootPath = useIsPath('/tenders', { endsWith: true });
  const isOnTenderDocumnets = useIsPath('/documents');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isPannelExapnded, setIsPannelExapnded] = useState<boolean>(true);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [onlyUnassignedFolders, setOnlyUnassignedFolders] = useState<boolean>(
    true
  );
  const [bottomViewHeight, setBottomViewHeight] = useState(0);
  const [breadcrumbs, setBreadcrumbs] = useState<any[]>([]);
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const filesQuery = useFiles(
    {
      folderId: String(
        selectedItem?.dbFile?.folder_id ??
          selectedItem?.dbAttachment?.folder_id ??
          ''
      ),
    },
    {
      enabled:
        !isOnTenders &&
        !!(
          selectedItem?.dbFile?.folder_id ??
          selectedItem?.dbAttachment?.folder_id
        ),
    }
  );
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const userQuery = useUser(
    {
      user_id: currentUserId ?? undefined,
      project_id: Number(
        selectedItem?.dbAttachment?.project_id ??
          selectedItem?.dbFile?.project_id ??
          projectId ??
          0
      ),
    },
    {
      enabled: !!currentUserId,
    }
  );
  const resizeObserverRef = useRef(
    new ResizeObserver(
      debounce(([resizeEntry]) => {
        setBottomViewHeight(resizeEntry.contentRect.height);
      }, 50)
    )
  );
  const currentFile = useMemo(() => {
    if (selectedItem?.dbFile) {
      return filesQuery?.data?.find((v) => v.id === selectedItem.dbFile.id);
    }
    return null;
  }, [filesQuery?.data, selectedItem]);

  const permissionLevel = useMemo(() => {
    if (!selectedItem) return 0;
    const parentId = Number(selectedItem.parentId);
    const parent = foldersQuery.data?.find((folder) => folder.id === parentId);
    return parent?.permission || 0;
  }, [selectedItem, foldersQuery.data]);

  useEffect(() => {
    if (selectedItem && !isActive) setIsActive(true);
    getUploadedBy();
  }, [selectedItem]);
  useEffect(() => {
    setBreadcrumbs(
      JSON.parse(sessionStorage.getItem('breadcrumbPath') || '[]')
    );
  }, [window.location.pathname, selectedItem?.id]);

  // Computed values after all hooks
  const isOnTenderRoot =
    isOnTenderRootPath &&
    (projectQuery?.data?.is_tender_admin || projectQuery?.data?.is_superuser);

  const isOnUnassignedTenderFolders =
    window.location.pathname.includes('/tender-folders/') &&
    !window.location.pathname.includes('/tenders/');

  const isTenderContext = isOnTenders || isOnMyTenders;

  const folderIdForFiles =
    selectedItem?.dbFile?.folder_id ?? selectedItem?.dbAttachment?.folder_id;

  const parentIdSafe =
    selectedItem?.parentId ??
    selectedItem?.dbAttachment?.folder_id ??
    selectedItem?.dbFile?.folder_id ??
    '';

  const projectIdForUser =
    selectedItem?.dbAttachment?.project_id ??
    selectedItem?.dbFile?.project_id ??
    projectId ??
    0;

  // ===== COMPUTED VALUES (after all hooks) =====
  const tagsData = archiveSettings.data?.resTags;
  const isTagsOn = archiveSettings?.data?.resArchiveSettings?.is_tags_on;

  const tags = currentFile ? getTagsForFile(currentFile?.tags, tagsData) : [];
  const selectedTags =
    currentFile && tagsData
      ? tagsData.filter((tag) => currentFile?.tags?.includes(tag.id))
      : [];

  const maxVisibleTags = 4;
  const hiddenCount =
    tags && tags.length > maxVisibleTags ? tags.length - maxVisibleTags : 0;
  const theme = doReturnTheme(currentTheme);

  const showDownloadButton = permissionLevel >= RIGHTS_LEVEL.OPEN;
  const isTenderShareRoot = !!selectedItem?.__share;

  // ===== HELPER FUNCTIONS =====
  const getUploadedBy = () => {
    if (selectedItem) {
      // Handle classic registrations first
      if ((selectedItem as any).classicRegistration) {
        setCurrentUserId(
          Number((selectedItem as any).classicRegistration.created_by || null)
        );
      } else if (selectedItem.moduleFile) {
        setCurrentUserId(null);
      } else if (selectedItem.folder) {
        setCurrentUserId(
          selectedItem.dbFolder?.created_by ??
            selectedItem.dbTenderFolder?.created_by ??
            null
        );
      } else if (selectedItem.dbFile) {
        setCurrentUserId(Number(selectedItem.dbFile.created_by || null));
      } else if (fileVersionsContext[selectedItem?.id]) {
        return fileVersionsContext[selectedItem?.id]?.created_by;
      } else if (selectedItem.dbFile?.versions) {
        setCurrentUserId(selectedItem.dbFile.created_by);
      } else if (selectedItem.dbAttachment) {
        setCurrentUserId(selectedItem.dbAttachment.created_by);
      }
    } else {
      return setCurrentUserId(null);
    }
  };

  // ===== OTHER HELPER FUNCTIONS =====
  const getUploadedDate = () => {
    // Handle classic registrations first
    if ((selectedItem as any).classicRegistration) {
      return formatDate(
        (selectedItem as any).classicRegistration.created_at,
        'long'
      );
    } else if (selectedItem.folder) {
      return formatDate(selectedItem.createdAt, 'long');
    } else if (selectedItem.dbFile) {
      return formatDate(selectedItem.dbFile.created_at, 'long');
    } else if (selectedItem.hasOwnProperty('dbModuleFile')) {
      return formatDate(
        (selectedItem.dbModuleFile as DbModuleFile).created_at,
        'long'
      );
    } else if (selectedItem.hasOwnProperty('dbAttachment')) {
      return formatDate(selectedItem.dbAttachment.created_at, 'long');
    } else {
      return '-';
    }
  };

  const getFileSize = () => {
    if (selectedItem.folder) {
      return '-';
    } else if (fileVersionsContext[selectedItem.id]) {
      return formatBytes(fileVersionsContext[selectedItem.id]?.size || 0, 1);
    } else if (selectedItem.dbFile) {
      const files = selectedItem.dbFile.files || [];
      const last = files[files.length - 1];
      const size =
        (last && (last.size ?? last.file_size)) ??
        selectedItem.dbFile.size ??
        0;
      return formatBytes(size, 1);
    } else if (selectedItem.dbAttachment) {
      const files = selectedItem.dbAttachment.files || [];
      const last = files[files.length - 1];
      const size =
        (last && (last.size ?? last.file_size)) ??
        selectedItem.dbAttachment.size ??
        0;
      return formatBytes(size, 1);
    } else {
      return '-';
    }
  };

  const handleDownload = () => {
    if (selectedItem.folder) {
      // const hidePrepMsg = message.loading(
      //   intl.formatMessage({ id: 'download-preparing' }),
      //   0
      // );
      // activate download progress panel
      changeDownloadingActive(true);
      changeDownloadingState(DownloadingState.PREPARING);
      downloadFolder({
        folder: selectedItem.dbFolder as DbFolder,
        allFolders: foldersQuery.data || [],
        fileVersionsContext,
        onDownloadStarted: () => {
          // hidePrepMsg();
          setTimeout(() =>
            message.info(intl.formatMessage({ id: 'download-starting' }))
          );
        },
        onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
      }).catch((err) => {
        appLog('downloadFolder -> err', err);
        // hidePrepMsg && hidePrepMsg();
        message.error(intl.formatMessage({ id: 'file-download-failed' }));
      });
    } else {
      const versions = (selectedItem.dbFile as DbFile).versions || [];

      const selectedIndex = fileVersionsContext[selectedItem.id]
        ? versions.findIndex(
            (opt) => opt.id === fileVersionsContext[selectedItem.id]?.id
          )
        : 0;
      const selectedVersion = versions[selectedIndex] || versions[0];
      downloadFileLastVersion(selectedItem.dbFile, fileVersionsContext).catch(
        (err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        }
      );
    }
  };

  const getViewButton = () => {
    if (!selectedItem) {
      return null;
    }

    if (selectedItem.folder) return null;

    const fileIcon = fileIcons[selectedItem.extension] || fileIcons.default;

    return (
      <Fragment>
        <IconButton
          icon={[fileIcon.iconStyle as IconPrefix, fileIcon.icon as IconName]}
          customStyle={{ fontSize: 18 }}
          color={fileIcon.color}
          tooltip={
            fileIcon.icon === 'file'
              ? intl.formatMessage({ id: 'file' }, { type: '' })
              : intl.formatMessage({ id: fileIcon.icon })
          }
          click={() => {
            if (selectedItem.dbFile) {
              setFileToView(selectedItem.dbFile);
            }
            if (selectedItem.dbModuleFile) {
              setFileToView(selectedItem.dbModuleFile);
            }
          }}
        />
        <span>({selectedItem.extension})</span>
      </Fragment>
    );
  };

  const pannelHeader = (
    <div css={style.detailsPaneHeader}>
      <div
        css={style.detailsTxtContainer}
        onClick={() =>
          isActive
            ? setIsPannelExapnded((prevState) => !prevState)
            : setIsActive(true)
        }>
        {intl.formatMessage({ id: 'details' })}
        <FontAwesomeIcon
          css={style.chevronIcon}
          icon={!isPannelExapnded ? 'chevron-down' : 'chevron-up'}
        />
      </div>

      {isActive && selectedItem && (
        <IconButton
          click={() => isActive && setIsActive(false)}
          iconStyle="far"
          icon="times"
          customStyle={style.closeBtn}
          tooltip={intl.formatMessage({ id: 'close' })}
          backgroundOff
        />
      )}
    </div>
  );

  const unassignedPathFolderId =
    currentFolderId ||
    rootFolderId ||
    (parentIdSafe !== 'null' ? parentIdSafe : '');

  const pathText = isOnUnassignedTenderFolders
    ? getPath(foldersQuery.data, unassignedPathFolderId)
    : isTenderContext
    ? (isOnTenderDocumnets ? breadcrumbs.slice(1) : breadcrumbs)
        .map(({ name }) => name)
        .join('/')
    : getPath(foldersQuery.data, parentIdSafe);

  const archiveText =
    '.../' +
    breadcrumbs
      .slice(2)
      .map(({ name }) => name)
      .join('/');

  const isWithArchivePath = breadcrumbs.length > 2;

  const breadcrambsMergedPath = (isOnTenderDocumnets
    ? breadcrumbs.slice(1)
    : breadcrumbs
  )
    .map(({ name }) => name)
    .join('/');

  const breadcrambsMergedArchivePath =
    '.../' +
    breadcrumbs
      .slice(2)
      .map(({ name }) => name)
      .join('/');

  const pannelHeaderTender = (
    <div css={style.detailsTenderPaneHeader}>
      {isOnTenderRoot && !selectedItem && (
        <div css={style.detailsTenderPaneHeaderContent}>
          <div css={style.detailsTenderPaneHeaderTitleCentered}>
            <NavIcon
              customStyle={{ width: '45px', height: '22px' }}
              icon={ModuleTenderHammerNoColor}
            />
            {intl.formatMessage({ id: 'tender-folder' })}
          </div>
          <div css={style.detailsTenderPaneHeaderRight}>
            <Checkbox
              checked={onlyUnassignedFolders}
              onChange={(e) => setOnlyUnassignedFolders(e.target.checked)}>
              {intl.formatMessage({ id: 'only-unassigned-folders' })}
            </Checkbox>
          </div>
        </div>
      )}

      <div
        css={style.detailsTenderTxtContainer}
        onClick={() => setIsActive((prevState) => !prevState)}>
        {intl.formatMessage({ id: 'details' })}
        <FontAwesomeIcon
          css={style.chevronIcon}
          icon={!isActive ? 'chevron-down' : 'chevron-up'}
        />
      </div>
    </div>
  );
  const tableScroll: Required<FileTableProps>['scroll'] = {
    // satisfies sum of fixed width columns + reasonable dynamic "name" column
    x: 1065,
    // 141px is topBar + folderNameBar + tableHeader + scrollbar
    y: `calc(100vh - 121px - ${bottomViewHeight}px)`,
  };

  const createdByName =
    (selectedItem as any)?.classicRegistration?.created_by_name ||
    (selectedItem?.moduleFile && selectedItem.dbModuleFile?.created_by) ||
    (selectedItem?.dbFile && selectedItem.dbFile?.created_by_name) ||
    userQuery.data?.[0]?.user_name ||
    '';

  const renderTenderShareDetails = () => {
    if (!selectedItem || !selectedItem.__share) return null;

    const share = selectedItem.__share;
    const project = share.project;
    const reg = share.registration;

    if (!isPannelExapnded) {
      return (
        <div css={style.panelContainer}>
          <div css={{ width: '30%' }}>
            <span css={style.text}>
              <div css={style.div}>
                <b>{intl.formatMessage({ id: 'name' })}: </b>
                <span>{selectedItem.name}</span>
              </div>
              <div css={style.div}>
                <b>{intl.formatMessage({ id: 'description' })}: </b>
                <span title={selectedItem.description}>
                  {selectedItem.description
                    ? selectedItem.description.length > 50
                      ? `${selectedItem.description.substring(0, 50)}...`
                      : selectedItem.description
                    : '-'}
                </span>
              </div>
              {reg?.registration_reference && (
                <div css={style.div}>
                  <b>{intl.formatMessage({ id: 'reference' })}: </b>
                  <span>{reg.registration_reference}</span>
                </div>
              )}
              <div css={style.div}>
                <b>{intl.formatMessage({ id: 'project' })}: </b>
                <span>{project?.name || '-'}</span>
              </div>
            </span>
          </div>
          <div css={{ width: '40%' }}>
            <span>
              <div css={style.div}>
                <b>{intl.formatMessage({ id: 'share-active-from' })}: </b>
                <span>
                  {share.share_active_from
                    ? formatDate(share.share_active_from, 'long')
                    : '-'}
                </span>
              </div>
              <div css={style.div}>
                <b>{intl.formatMessage({ id: 'share-expires-at' })}: </b>
                <span>
                  {share.share_expires_at
                    ? formatDate(share.share_expires_at, 'long')
                    : '-'}
                </span>
              </div>
              <div css={style.div}>
                <b>{intl.formatMessage({ id: 'last-update' })}: </b>
                <span>
                  {selectedItem.updatedAt
                    ? formatDate(selectedItem.updatedAt, 'long')
                    : '-'}
                </span>
              </div>
            </span>
          </div>
          <div css={{ width: '30%' }}></div>
        </div>
      );
    }

    return (
      <div css={style.panelContainer}>
        <div css={{ width: '30%' }}>
          <span css={style.text}>
            <div css={style.div}>
              <b>{intl.formatMessage({ id: 'name' })}: </b>
              <span>{selectedItem.name}</span>
            </div>
            {reg?.registration_reference && (
              <div css={style.div}>
                <b>{intl.formatMessage({ id: 'reference' })}: </b>
                <span>{reg.registration_reference}</span>
              </div>
            )}
          </span>
        </div>
        <div css={{ width: '40%' }}>
          <span css={style.text}>
            <div css={style.div}>
              <b>{intl.formatMessage({ id: 'share-active-from' })}: </b>
              <span>
                {share?.share_active_from
                  ? formatDate(share.share_active_from, 'long')
                  : '-'}
              </span>
            </div>
          </span>
        </div>
        <div css={{ width: '30%' }}></div>
      </div>
    );
  };

  // Aliasing the prop to PascalCase to use as a Component
  const TenderPropertiesTable = tenderPropertiesTable as React.ElementType;

  return (
    <div css={style.container}>
      <Collapse
        bordered={false}
        css={style.properties}
        activeKey={
          (selectedItem && isActive) || (isOnTenderRoot && isActive)
            ? ['properties-panel']
            : ['']
        }
        expandIconPosition="right"
        onChange={(...args) => {
          onCollapse && onCollapse(...args);
        }}>
        <Panel
          forceRender={true}
          css={style.panleHeader}
          header={''}
          showArrow={false}
          extra={isOnTenderRoot ? pannelHeaderTender : pannelHeader}
          key="properties-panel">
          {isOnTenderRoot && !selectedItem && TenderPropertiesTable ? (
            // !!! Fix: Render as Component Element instead of calling function directly
            <TenderPropertiesTable
              onlyUnassignedFolders={onlyUnassignedFolders}
            />
          ) : isTenderShareRoot ? (
            renderTenderShareDetails()
          ) : !isPannelExapnded ? (
            <div css={style.panelContainer}>
              <div css={{ width: '30%' }}>
                <span css={style.text}>
                  <div css={style.div}>
                    <b>{intl.formatMessage({ id: 'file-name' })}: </b>
                    <span>{selectedItem ? selectedItem.name : ''}</span>
                  </div>
                  <div css={style.div}>
                    <b>{intl.formatMessage({ id: 'description' })}: </b>
                    <span>{selectedItem ? selectedItem.description : ''}</span>
                  </div>
                  <div css={style.div}>
                    <b>{intl.formatMessage({ id: 'format' })}: </b>
                    <span>{selectedItem ? getViewButton() : ''}</span>
                  </div>
                  {!isOnTenderDocumnets && (
                    <div css={style.div}>
                      <b>{intl.formatMessage({ id: 'versions' })}: </b>
                      <span>
                        {selectedItem
                          ? selectedItem.dbFile?.versions?.length
                          : ''}
                      </span>
                    </div>
                  )}
                  {isTagsOn && (
                    <div css={[style.div, { display: 'flex' }]}>
                      <b>{intl.formatMessage({ id: 'tags' })}: </b>
                      <span>
                        <div
                          style={{
                            display: 'flex',
                            marginLeft: '7px',
                          }}>
                          {selectedTags
                            ?.slice(0, maxVisibleTags)
                            .map((tagItem, index) => {
                              const tag = String(tagItem.title);
                              return (
                                <TagAntd
                                  style={{
                                    color:
                                      tagItem.state === DB_STATE.REMOVED
                                        ? theme.colors.darkGray
                                        : theme.colors.text.white,
                                    background:
                                      tagItem.state === DB_STATE.REMOVED
                                        ? theme.colors.lightGray
                                        : '#1f5fa6',
                                  }}
                                  key={index}>
                                  <Tooltip title={tag}>
                                    {tag.length > 10
                                      ? `${tag.substring(0, 10)}...`
                                      : tag}
                                  </Tooltip>
                                </TagAntd>
                              );
                            })}
                          {hiddenCount > 0 && (
                            <Popover
                              overlayStyle={{ minWidth: 100, maxWidth: 250 }}
                              content={
                                <div
                                  css={{
                                    padding: '10px',
                                  }}>
                                  <h3>
                                    <b>{intl.formatMessage({ id: 'tags' })}</b>
                                  </h3>
                                  {selectedTags?.map((tag, index) => (
                                    <TagAntd
                                      style={{
                                        color:
                                          tag.state === DB_STATE.REMOVED
                                            ? theme.colors.darkGray
                                            : theme.colors.text.white,
                                        background:
                                          tag.state === DB_STATE.REMOVED
                                            ? theme.colors.lightGray
                                            : '#1f5fa6',
                                        marginBottom: '4px',
                                      }}
                                      key={index}
                                      color={'#1f5fa6'}>
                                      {tag.title}
                                    </TagAntd>
                                  ))}
                                </div>
                              }>
                              <TagAntd>+{hiddenCount}</TagAntd>
                            </Popover>
                          )}
                        </div>
                      </span>
                    </div>
                  )}
                </span>
              </div>
              <div css={{ width: !isOnTenderDocumnets ? '30%' : '40%' }}>
                <span>
                  {createdByName && (
                    <div css={style.div}>
                      <b>{intl.formatMessage({ id: 'uploaded-by' })}: </b>
                      <span>{createdByName}</span>
                    </div>
                  )}
                  <div css={style.div}>
                    <b>{intl.formatMessage({ id: 'size' })}: </b>
                    <span>{selectedItem ? getFileSize() : ''}</span>
                  </div>
                  <div css={style.div}>
                    <b>{intl.formatMessage({ id: 'uploaded-date' })}: </b>
                    <span>{selectedItem ? getUploadedDate() : ''}</span>
                  </div>
                </span>
                {selectedItem ? (
                  <Fragment>
                    <div css={style.div}>
                      <Link
                        style={
                          window.sessionStorage.getItem('appState') ===
                          'timeMachine'
                            ? { color: '#08c1c1' }
                            : {}
                        }
                        to={
                          isOnUnassignedTenderFolders
                            ? `/projects/${projectId}/archive/${unassignedPathFolderId}`
                            : isTenderContext
                            ? window.location.pathname
                            : `/projects/${projectId}/archive/${
                                parentIdSafe !== 'null' ? parentIdSafe : ''
                              }`
                        }>
                        <b>
                          {intl.formatMessage({
                            id: isTenderContext ? 'pathTender' : 'path',
                          })}
                          :{' '}
                        </b>
                        <span>{selectedItem ? pathText : ''}</span>
                      </Link>
                    </div>
                    {isOnTenderDocumnets && isWithArchivePath && (
                      <div css={style.div}>
                        {isOnMyTenders ? (
                          // my-tenders: -no archive link
                          <span
                            style={
                              window.sessionStorage.getItem('appState') ===
                              'timeMachine'
                                ? { color: '#08c1c1' }
                                : {}
                            }>
                            <b>
                              {intl.formatMessage({ id: 'archive-location' })}:{' '}
                            </b>
                            <span>{selectedItem ? archiveText : ''}</span>
                          </span>
                        ) : (
                          // project-side: all works
                          <Link
                            style={
                              window.sessionStorage.getItem('appState') ===
                              'timeMachine'
                                ? { color: '#08c1c1' }
                                : {}
                            }
                            to={`/projects/${projectId}/archive/${
                              parentIdSafe !== 'null' ? parentIdSafe : ''
                            }`}>
                            <b>
                              {intl.formatMessage({ id: 'archive-location' })}:{' '}
                            </b>
                            <span>{selectedItem ? archiveText : ''}</span>
                          </Link>
                        )}
                      </div>
                    )}
                  </Fragment>
                ) : (
                  <div css={style.div}>
                    <b>{intl.formatMessage({ id: 'path' })}: </b>
                  </div>
                )}
              </div>

              {!isOnTenderDocumnets && (
                <div css={style.previewContainer}>
                  {selectedItem &&
                    !selectedItem.folder &&
                    !selectedItem.moduleFile &&
                    selectedItem.dbFile?.versions && (
                      <div css={style.versionContainer}>
                        <b>
                          {intl.formatMessage({ id: 'change-version' })}:&nbsp;
                        </b>
                        <VersionSelect file={selectedItem.dbFile} />
                      </div>
                    )}
                  {selectedItem && showDownloadButton && (
                    <IconButton
                      icon={[
                        'far',
                        selectedItem.folder
                          ? 'folder-download'
                          : 'file-download',
                      ]}
                      customStyle={style.icons}
                      tooltip={intl.formatMessage({
                        id: selectedItem.folder
                          ? 'download-folder'
                          : 'download-file',
                      })}
                      click={handleDownload}
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div css={style.panelContainer}>
              <div css={{ width: '30%' }}>
                <span css={style.text}>
                  {createdByName && (
                    <div css={style.div}>
                      <b>{intl.formatMessage({ id: 'uploaded-by' })}: </b>
                      <span>{createdByName}</span>
                    </div>
                  )}
                  <div css={style.div}>
                    <b>{intl.formatMessage({ id: 'size' })}: </b>
                    <span>{selectedItem ? getFileSize() : ''}</span>
                  </div>
                </span>
              </div>
              <div css={{ width: !isOnTenderDocumnets ? '30%' : '40%' }}>
                <span>
                  <div css={style.div}>
                    <b>{intl.formatMessage({ id: 'uploaded-date' })}: </b>
                    <span>{selectedItem ? getUploadedDate() : ''}</span>
                  </div>
                </span>
                {!isOnTenderDocumnets &&
                  (selectedItem ? (
                    <div css={style.div}>
                      <Link
                        style={
                          window.sessionStorage.getItem('appState') ===
                          'timeMachine'
                            ? { color: '#08c1c1' }
                            : {}
                        }
                        to={
                          isOnUnassignedTenderFolders
                            ? `/projects/${projectId}/archive/${
                                currentFolderId ||
                                rootFolderId ||
                                (selectedItem.parentId !== 'null'
                                  ? selectedItem.parentId
                                  : '')
                              }`
                            : isTenderContext
                            ? window.location.pathname
                            : `/projects/${projectId}/archive/${
                                selectedItem.parentId !== 'null'
                                  ? selectedItem.parentId
                                  : ''
                              }`
                        }>
                        <b>
                          {intl.formatMessage({
                            id: isTenderContext ? 'pathTender' : 'path',
                          })}
                          :{' '}
                        </b>
                        <span>
                          {selectedItem
                            ? isOnUnassignedTenderFolders
                              ? getPath(
                                  foldersQuery.data,
                                  unassignedPathFolderId
                                )
                              : isTenderContext
                              ? breadcrambsMergedPath
                              : getPath(
                                  foldersQuery.data,
                                  selectedItem.parentId
                                )
                            : ''}
                        </span>
                      </Link>
                    </div>
                  ) : (
                    <div css={style.div}>
                      <b>{intl.formatMessage({ id: 'path' })}: </b>
                    </div>
                  ))}
              </div>
              {!isOnTenderDocumnets && (
                <div css={style.previewContainer}>
                  {selectedItem &&
                    !selectedItem.folder &&
                    !selectedItem.moduleFile &&
                    selectedItem.dbFile?.versions && (
                      <div css={style.versionContainer}>
                        <b>
                          {intl.formatMessage({ id: 'change-version' })}:&nbsp;
                        </b>
                        <VersionSelect file={selectedItem.dbFile} />
                      </div>
                    )}
                  {selectedItem && showDownloadButton && (
                    <IconButton
                      icon={[
                        'far',
                        selectedItem.folder
                          ? 'folder-download'
                          : 'file-download',
                      ]}
                      customStyle={style.icons}
                      tooltip={intl.formatMessage({
                        id: selectedItem.folder
                          ? 'download-folder'
                          : 'download-file',
                      })}
                      click={handleDownload}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </Panel>
      </Collapse>
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    overflow: 'hidden',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
  },
  div: {
    color: theme.colors.text.primary,
    padding: '6px 0px',
  },
  properties: {
    color: theme.colors.text.secondary,
    backgroundColor:
      window.sessionStorage.getItem('appState') !== 'superUser'
        ? `${theme.colors.primaryLight} !important`
        : `${theme.colors.lightGray} !important`,
  },
  panleHeader: {
    color: `${theme.colors.text.secondary} !important`,
  },
  panelContainer: {
    marginTop: '-35px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    alignSelf: 'flex-end',
    // paddingRight: 200,
    width: '30%',
  },
  versionContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    minWidth: 150,
  },
  icons: {
    color: theme.colors.primary,
    fontSize: '18px',
    margin: '4px 10px 0 10px',
  },
  closeBtn: {
    color: '#000000',
    fontSize: '18px',
    transition: '0.3s',
    cursor: 'pointer',
    marginLeft: 20,
    right: 0,
  },
  detailsPaneHeaderTender: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    width: '78vw',
    marginRight: -20,
    alignItems: 'center',
    height: 60,
    marginTop: -20,
    marginBottom: -20,
  },

  detailsPaneHeader: {
    display: 'inline-flex',
    width: '78vw',
    paddingRight: '28px',
    alignItems: 'center',
    height: 60,
    marginTop: -20,
    marginBottom: -20,
    justifyContent: 'flex-end',
  },
  detailsPaneHeaderTitle: {
    fontWeight: 'bold',
    gridColumn: '2 / 3',
    display: 'flex',
    justifyContent: 'center',
  },
  detailsTxtContainer: {
    gridColumn: '3 / 4',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  chevronIcon: {
    marginLeft: 10,
  },
  detailsTenderPaneHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginLeft: '15%',
  },
  detailsTenderPaneHeaderContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  detailsTenderPaneHeaderTitleCentered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  detailsTenderPaneHeaderRight: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

export default PropertiesPanel;
